import { useState } from "react";
import { useAsync } from "../useAsync.js";
import { isValidId } from "../ids.js";
import { api, isAlreadyExists, type Template } from "../api.js";
import { SkeletonTable } from "../Skeleton.js";
import { RefreshButton } from "../RefreshButton.js";
import { adPositions } from "@addressium/domain";

export function Templates({ org }: { org: string }) {
  const list = useAsync(() => api.templates(org), [org]);
  const [templateId, setTemplateId] = useState("");
  const [name, setName] = useState("");
  const [source, setSource] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  /**
   * The saved template being edited, or null for a new one (#347). An edit
   * bumps the version; a NEW template whose id is taken is refused rather than
   * becoming the next version of somebody else's template.
   */
  const [editingId, setEditingId] = useState<string | null>(null);
  const edit = (t: Template) => {
    if (t.mode !== "raw_html") {setMsg("This legacy template needs an HTML export from your email builder before it can be edited."); return;}
    setTemplateId(t.templateId); setName(t.name); setSource(t.source); setEditingId(t.templateId);
    setMsg(""); setPreview(null);
  };
  const reset = () => {setTemplateId("");setName("");setSource("");setMsg("");setPreview(null);setEditingId(null);};
  const save = async () => {
    setMsg("");setBusy(true);
    try {
      const saved = await api.saveTemplate({orgId:org,templateId:templateId.trim(),name:name.trim(),mode:"raw_html",source,intent:editingId ? "update" : "create"});
      setSource(saved.source);setPreview(saved.source);setMsg(`Saved "${saved.templateId}" (v${saved.version}).`);
      setEditingId(saved.templateId);
      void list.refetch();
    } catch(e) {
      setMsg(isAlreadyExists(e)
        ? `A template with id "${templateId.trim()}" already exists. Choose a different id, or use Edit on the existing one.`
        : String(e));
    } finally {setBusy(false);}
  };
  const valid = isValidId(templateId.trim()) && !!name.trim() && !!source.trim();
  return <div>
    <div className="pagehead">
      <div><h1>Templates</h1><p>HTML / CSS templates exported from Beefree or your email builder.</p></div>
      <RefreshButton refreshing={list.refreshing} disabled={list.loading} onClick={() => void list.refetch()} />
    </div>
    <p className="muted">Responsive CSS, Outlook markup and newsletter story markers are preserved. Merge values are escaped and editorial links are tracked.</p>
    <p className="muted">Compose loads a copy of a saved template. Saving changes here does not update a body already loaded in Compose or any scheduled campaign, including recurring sends.</p>
    {/* Drip steps are the exception, by design (#368): each send loads its
        step's template fresh, so in-flight readers follow the new version. */}
    <p className="err">Drip sequences are different: they use the saved template at each send. Saving here changes the next email for every reader already in a sequence that uses this template.</p>
    {list.loading && <SkeletonTable rows={4} />}
    {list.error && <p className="err">{list.error}</p>}
    {!!list.data?.length && <div className="card"><table>
      <thead><tr><th>Template</th><th>Format</th><th>Version</th><th></th></tr></thead>
      <tbody>{list.data.map(t => <tr key={t.templateId}>
        <td className="t-strong">{t.name} <span className="muted">({t.templateId})</span></td>
        <td>{t.mode === "raw_html" ? "HTML / CSS" : "Legacy template"}</td><td>v{t.version}</td>
        <td><button className="btn ghost" onClick={() => edit(t)}>Edit</button></td>
      </tr>)}</tbody>
    </table></div>}
    <div className="card">
      <div className="muted">{editingId ? `Editing ${editingId}` : "New template"}</div>
      <div style={{display:"flex",gap:8,marginTop:8}}>
        <input value={templateId} onChange={e=>setTemplateId(e.target.value)} placeholder="template id" disabled={busy || editingId !== null} />
        <input value={name} onChange={e=>setName(e.target.value)} placeholder="Display name" disabled={busy} />
      </div>
      <label htmlFor="template-source" style={{marginTop:12}}>HTML / CSS source</label>
      <textarea id="template-source" value={source} onChange={e=>{setSource(e.target.value);setPreview(null);}} rows={16} style={{width:"100%",fontFamily:"monospace"}} placeholder="Paste your exported HTML here" />
      {/* Read off the HTML, not typed (legacy placement): the send fills only
          markers that exist, so a hand-kept list could promise a position the
          template does not have. */}
      <AdPositionsLine html={source} />
      <div style={{display:"flex",gap:10,marginTop:8}}>
        <button className="btn" disabled={!valid || busy} onClick={save}>{busy ? "Saving…" : "Save template"}</button>
        <button className="btn ghost" disabled={!source.trim()} onClick={()=>setPreview(source)}>Preview HTML</button>
        <button className="btn ghost" onClick={reset} disabled={busy}>New</button>
      </div>
      {msg && <p className={msg.startsWith("Saved") ? "muted" : "err"}>{msg}</p>}
      {preview && <iframe title="preview" sandbox="" srcDoc={preview} style={{width:"100%",height:480,marginTop:12,border:"1px solid #ddd",background:"#fff"}} />}
    </div>
  </div>;
}

/** Which legacy ad positions this HTML offers, shown live as it is edited. */
function AdPositionsLine({ html }: { html: string }) {
  if (!html.trim()) return null;
  const at = adPositions(html);
  const found = [
    at.marquee && "Marquee ({{MARQUEEAD}})",
    at.footer && "Footer ({{SAFERTB}})",
    at.body && "Body ads, one after each main story",
  ].filter(Boolean);
  return <p className="muted" data-testid="ad-positions">
    {found.length
      ? <>Ad positions in this template: {found.join(" · ")}. Which ad goes in each is set per newsletter in Compose &amp; schedule, and its HTML under Ad tags.</>
      : <>No ad positions in this template. Ads are placed at {"{{MARQUEEAD}}"} (top), {"{{SAFERTB}}"} (footer), and between stories inside MAIN STORY / BODY STORY regions.</>}
  </p>;
}
